import React, { useState, useEffect, useRef } from 'react';
import { Send } from 'lucide-react'; // Import ikony

const SendToPrintPopup = ({
  isOpen,
  onClose,
  backendUrl,
  publicKey,
  initialEmail = "", // Odbieramy początkowy email
  onSuccess, // Funkcja do wywołania po sukcesie (np. aktualizacja stanu order)
}) => {
  const [sendEmail, setSendEmail] = useState("");
  const [isSendingToPrint, setIsSendingToPrint] = useState(false);
  const [sendPopupValidationError, setSendPopupValidationError] = useState("");
  const sendNoteRef = useRef("");

  // Ustawiamy początkowy email, gdy popup się otwiera
  useEffect(() => {
    if (isOpen) {
      setSendEmail(initialEmail);
      sendNoteRef.current = ""; // Resetuj notatkę przy otwarciu
      setSendPopupValidationError(""); // Resetuj błędy walidacji
      setIsSendingToPrint(false); // Resetuj stan wysyłania
    }
  }, [isOpen, initialEmail]);

  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  // Logika wysyłania (zaadaptowana z ClientOrderPanel i useClientOrderActions)
  const handleSendToPrintInternal = async () => {
    setSendPopupValidationError("");
    if (!sendEmail.trim() || !sendEmail.includes("@")) {
      setSendPopupValidationError("Niepoprawny adres e-mail!");
      return;
    }
    setIsSendingToPrint(true);

    try {
      const cleanedPublicKey = String(publicKey || '').replace(/[^a-zA-Z0-9]/g, '');

      // 1. Check/Update Email (jeśli się zmienił)
      // Pobieramy aktualne zamówienie, aby sprawdzić stary email - to może być problematyczne,
      // lepiej polegać na initialEmail lub uprościć i zawsze wysyłać PATCH z emailem.
      // Uproszczenie: Zawsze wysyłamy PATCH z emailem i statusami.
      /*
      const checkUrl = `${backendUrl}/client/orders/${cleanedPublicKey}`;
      const checkRes = await fetch(checkUrl);
      if (!checkRes.ok) throw new Error("Błąd pobierania zamówienia przed wysyłką.");
      const checkData = await checkRes.json();
      const oldEmail = checkData.customOrderId || "";
      if (oldEmail.trim() !== sendEmail.trim()) { ... } // Pomijamy ten krok dla uproszczenia
      */

      // 2. Pobierz aktualne pliki i zaktualizuj statusy + email + flagę sentToPrint
      const orderAgainUrl = `${backendUrl}/client/orders/${cleanedPublicKey}`;
      const orderAgainRes = await fetch(orderAgainUrl);
      if (!orderAgainRes.ok) throw new Error("Błąd pobierania zamówienia przed zmianą statusów.");
      const orderAgainData = await orderAgainRes.json();
      const oldFiles = orderAgainData.files || [];
      const newFilesWithAcceptedStatus = oldFiles.map((f) => ({ ...f, statusId: 1 })); // Zakładamy statusId=1 dla 'Zaakceptowany'

      const patchOrderUrl = `${backendUrl}/client/orders/${cleanedPublicKey}`;
      console.log(`[SendToPrintPopup] handleSendToPrint: Patching order: ${patchOrderUrl}`);
      const patchOrderRes = await fetch(patchOrderUrl, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          files: newFilesWithAcceptedStatus,
          customOrderId: sendEmail,
          sentToPrint: true
        })
      });
      if (!patchOrderRes.ok) throw new Error("Błąd aktualizacji zamówienia (statusy/email/flaga).");
      const updatedOrderData = await patchOrderRes.json();

      // 3. Uruchom trigger
      const triggerUrl = `${backendUrl}/client/orders/${cleanedPublicKey}/run-trigger`;
      const triggerBody = { triggerName: "nowe_zamowienie", tresc_od_klienta: sendNoteRef.current };
      console.log(`[SendToPrintPopup] handleSendToPrint: Running trigger: ${triggerUrl}`);
      const trigRes = await fetch(triggerUrl, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(triggerBody)
      });
      if (!trigRes.ok) console.error("Błąd uruchamiania triggera mailowego, ale zamówienie zostało zaakceptowane.");

      // 4. Wywołaj onSuccess callback z zaktualizowanymi danymi zamówienia
      if (onSuccess) {
        onSuccess({ ...updatedOrderData, sentToPrint: true }); // Przekaż zaktualizowane dane
      }
      onClose(); // Zamknij popup

    } catch (err) {
      console.error("[SendToPrintPopup] handleSendToPrint =>", err);
      setSendPopupValidationError(err.message || "Nie udało się wysłać zamówienia do druku.");
    } finally {
      setIsSendingToPrint(false);
    }
  };


  if (!isOpen) {
    return null; // Nie renderuj nic, jeśli popup nie jest otwarty
  }

  return (
    <div className="popup-overlay visible" onClick={handleOverlayClick}>
      <div className="popup-content">
        <div className="popup-header">
          <Send size={24} className="popup-icon" style={{ color: '#28a745' }} /> {/* Ikona wysyłania */}
          <h3>Wyślij do druku</h3>
          <button onClick={onClose} className="popup-close-btn">&times;</button>
        </div>
        <div className="popup-body">
          <label style={{ display: 'block', marginBottom: 6, fontWeight: "bold", textAlign: 'left' }}>
            Adres e-mail:
          </label>
          <input
            type="email"
            value={sendEmail}
            onChange={(e) => setSendEmail(e.target.value)}
            style={{
              width: '100%',
              padding: 8,
              marginBottom: 12,
              border: "1px solid #ccc",
              borderRadius: 4,
              fontSize: "1rem",
              boxSizing: 'border-box'
            }}
            placeholder="np. jan.kowalski@example.com"
            disabled={isSendingToPrint}
          />

          <label style={{ display: 'block', marginBottom: 6, fontWeight: "bold", textAlign: 'left' }}>
            Informacje do zamówienia (np. ilości, dane kontaktowe):
          </label>
          <textarea
            rows={4}
            ref={sendNoteRef} // Używamy ref
            // defaultValue="" // Niepotrzebne, bo ref jest czyszczony w useEffect
            onChange={(e) => sendNoteRef.current = e.target.value} // Aktualizujemy ref przy zmianie
            style={{
              width: '100%',
              padding: 8,
              marginBottom: 12,
              border: "1px solid #ccc",
              borderRadius: 4,
              fontSize: "1rem",
              boxSizing: 'border-box'
            }}
            placeholder="Wpisz tutaj szczegóły zamówienia, np. liczbę sztuk, dane kontaktowe. Skontaktujemy się w celu potwierdzenia."
            disabled={isSendingToPrint}
          />
          {sendPopupValidationError && (
            <p style={{ color: 'red', marginTop: '10px', textAlign: 'left', fontSize: '0.9em' }}>{sendPopupValidationError}</p>
          )}
        </div>
        <div className="popup-footer">
          <button
            onClick={onClose}
            className="popup-btn popup-btn-cancel"
            disabled={isSendingToPrint}
          >
            Anuluj
          </button>
          <button
            onClick={handleSendToPrintInternal}
            className="popup-btn popup-btn-confirm"
            disabled={isSendingToPrint}
          >
            {isSendingToPrint ? (
              <>
                <div className="popup-spinner popup-spinner-button" style={{ marginRight: '8px', borderColor: 'rgba(255,255,255,0.3)', borderTopColor: '#fff' }}></div>
                Wysyłanie...
              </>
            ) : "Wyślij"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default SendToPrintPopup;
